import { TitleDialog } from './TitleDialog';
import * as React from 'react';
import {
  Button, Checkbox,
  Dialog,
  FormControl,
  FormControlLabel, FormLabel,
  Grid,
  MenuItem, Radio,
  RadioGroup,
  Select, SvgIcon,
  TextField, Typography
} from '@mui/material';
import { DialogRoot } from './DialogRoot';
import { Organisation } from '../types/organisation';
import { UploadFile, Cancel } from '@mui/icons-material';
import { useReducer, useRef, useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import axios from 'axios';
import { getApiBaseUrl, getAssetsBaseUrl } from '../../utils/env';
import { DownloadArea } from '../common/DownloadArea';

type DialogProps = {
  open: boolean,
  title: string,
  handleClose: () => void,
  organisation: Organisation,
}

type OrganisationAction = {
  type: string;
  payload: any
}

type OrganisationState = Organisation & {
  assign_to_all_admin: boolean,
  auto_send_clip_basic: boolean,
}

const reducer = (state: OrganisationState, action: OrganisationAction) => {
  switch (action.type) {
    case 'UPDATE':
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const requestFormOptions = () => {
  const success = ({ data }) => data;
  return Promise.all([
    axios.get(`${getApiBaseUrl()}/organisation/orgtype`).then(success),
    axios.get(`${getApiBaseUrl()}/organisation/integration-type`).then(success),
    axios.get(`${getApiBaseUrl()}/organisation/rightsholder`).then(success)
  ]);
};

export default function OrganisationForm({
  open,
  title,
  handleClose,
  organisation
}: DialogProps) {
  const [file, setCurrentFile] = useState<File>();
  const [state, dispatch] = useReducer<React.Reducer<OrganisationState, OrganisationAction>>(reducer, {
    ...organisation,
    assign_to_all_admin: true,
    // auto_send_clip_basic: false,
  });
  const [integrationOpts, setIntegrationOpts] = useState<{ id: number, description: string }[]>([]);
  const [orgTypeOpts, setOrgTypeOpts] = useState<{ id: number, type: string }[]>([]);
  const [rightsholderOpts, setRightsholderOpts] = useState<{ id: number, domain: string }[]>([]);
  const [autoGenerateDomain, setAutoGenerateDomain] = useState<boolean>(true);
  const dropArea = useRef<HTMLDivElement>();
  const inputFile = useRef<HTMLInputElement>(null);
  const dropFileCallback = (files: FileList | null) => {
    if (files && files.length > 0) {
      const [droppedFile] = files;
      if (!/^image\//.test(droppedFile.type)) {
        alert('Unsupported file type, please upload image');
        return;
      }
      setCurrentFile(droppedFile);
    }
  };

  useEffect(() => {
    requestFormOptions().then(([orgTypesOpts, integrationOpts, rightsholderOpts]) => {
      setOrgTypeOpts(orgTypesOpts);
      setIntegrationOpts(integrationOpts);
      setRightsholderOpts(rightsholderOpts);
      if (!state.id) {
        const {
          id: orgTypeId,
          type: orgType
        } = orgTypesOpts.find(({ type }) => /advertiser/i.test(type)) || {};
        const { id: integrationId } = integrationOpts.find(({ description }) => /ad served/i.test(description)) || {};
        dispatch({
          type: 'UPDATE',
          payload: {
            org_type: [{ id: orgTypeId, type: orgType }],
            integration_type: integrationId,
            rightsholders: rightsholderOpts.filter(({ is_premium }) => is_premium).map(({ id }) => id)
          }
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upload = async () => {
    if (!file) {
      return;
    }

    const formData = new FormData();
    formData.append('file', file as Blob);

    const {
      data: {
        putObjectSignedUrl, logoFilename
      }
    } = await axios.post(`${getApiBaseUrl()}/organisation/logo-signed-url`, {
      filename: file?.name,
      content_type: file.type,
    });

    await axios({
      method: 'put',
      url: putObjectSignedUrl,
      data: file,
      headers: {
        'Content-Type': file.type
      },
      onUploadProgress: (progressEvent) => {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        console.log(`Upload Progress: ${progress}%`);
      },
    }).catch((message) => {
      // todo
    });
    return logoFilename;
  };

  const submit = async (e) => {
    e.preventDefault();

    let logoFilename: string = state.logo_filename;
    if (file) {
      logoFilename = await upload();
      setCurrentFile(undefined);

      dispatch({
        type: 'UPDATE',
        payload: {
          logo_filename: logoFilename
        }
      });
    }

    const basePayload = {
      name: state.name,
      domain: state.domain,
      manually_approve_video: state.manually_approve_video,
      is_premium: state.is_premium,
      auto_send_clip_basic: state.auto_send_clip_basic,
      rightsholder_ids: state.rightsholders,
      assign_to_all_admin: state.assign_to_all_admin,
      logo_filename: logoFilename,
      org_types: state.org_type,
      rh_video_format: state.rh_video_format
    };

    // Only include integration_type_id for advertiser and publisher types
    const payload = state.org_type.some(({ type }) => /advertiser|publisher/i.test(type))
      ? { ...basePayload, integration_type_id: state.integration_type }
      : basePayload;

    if (state.id) {
      await axios.patch(`${getApiBaseUrl()}/organisation/${state.id}`, {
        ...payload,
        rightsholder_ids: payload.rightsholder_ids.length > 0 ? payload.rightsholder_ids : [state.id],
      });
    } else {
      await axios.post(`${getApiBaseUrl()}/organisation`, payload);
    }

    handleClose();
  };

  return (<Dialog title={title} open={open} onClose={handleClose} fullWidth={true} maxWidth={'md'}>
    <DialogRoot handleClose={handleClose}>
      <TitleDialog>{title}</TitleDialog>
      <form onSubmit={submit}>
        <Box sx={{ m: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <TextField id="name"
                           value={state.name}
                           required
                           label="Name"
                           onChange={({ target: { value } }) => {
                             dispatch({
                               type: 'UPDATE',
                               payload: {
                                 name: value
                               }
                             });
                             if (!state.id && autoGenerateDomain) {
                               dispatch({
                                 type: 'UPDATE',
                                 payload: {
                                   domain: value.replace(/[-\s]/g, '').toLowerCase()
                                 }
                               });
                             }
                           }}
                           variant="outlined"
                           type={'text'}
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <TextField id="domain"
                           required
                           value={state.domain}
                           onChange={({ target: { value } }) => {
                             if (autoGenerateDomain) setAutoGenerateDomain(false);
                             dispatch({
                               type: 'UPDATE',
                               payload: {
                                 domain: value
                               }
                             });
                           }}
                           disabled={Boolean(state.id)}
                           label="Domain/slug"
                           variant="outlined"
                           type={'text'}
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <FormLabel required id="type-radio-buttons-group-label">Type</FormLabel>
                <RadioGroup aria-labelledby="type-radio-buttons-group-label"
                            name="radio-buttons-group-type"
                            row>
                  {orgTypeOpts.filter(({ type }) => /advertiser/i.test(type)).map(({
                    id,
                    type
                  }) => (
                    <FormControlLabel
                      value={id}
                      key={type}
                      control={<Radio
                        required
                        checked={state.org_type.some(({ id: stateTypeId }) => stateTypeId === id)}
                        onChange={() => dispatch({
                          type: 'UPDATE', payload: {
                            org_type: [{ id, type }]
                          }
                        })}/>}
                      label={type}
                    />
                  ))}
                  {orgTypeOpts.filter(({ type }) => /rightsholder/i.test(type)).map(({
                    id,
                    type
                  }) => (
                    <FormControlLabel
                      value={id}
                      key={type}
                      control={<Radio
                        required
                        checked={state.org_type.some(({ type }) => /rightsholder/i.test(type)) && !state.org_type.some(({ type }) => /advertiser|publisher/i.test(type))}
                        onChange={() => dispatch({
                          type: 'UPDATE', payload: {
                            org_type: [
                              ...state.org_type.filter(({ type }) => !/advertiser|publisher/i.test(type)),
                              { id, type }
                            ].reduce<{
                              type: string;
                              id: number;
                            }[]>((acc, next) => {
                              if (!acc.some(({ type }) => type === next.type)) {
                                return [...acc, next];
                              }
                              return acc;
                            }, [])
                          }
                        })}/>}
                      label={type}
                    />
                  ))}
                  {orgTypeOpts.filter(({ type }) => /publisher/i.test(type)).map(({
                    id,
                    type
                  }) => (
                    <FormControlLabel
                      value={id}
                      key={type}
                      control={<Radio
                        required
                        checked={state.org_type.some(({ type }) => /publisher/i.test(type))}
                        onChange={() => dispatch({
                            type: 'UPDATE', payload: {
                            org_type: [{ id, type }]
                          }
                        })}/>}
                      label="Publisher (legacy)"
                    />
                  ))}
                </RadioGroup>
              </FormControl>
              {state.org_type.some(({ type }) => /advertiser/i.test(type)) && <>
                <FormControl fullWidth>
                  <FormControlLabel
                    control={<Checkbox
                      defaultChecked={state.org_type.filter(({ type }) => /advertiser|rightsholder/i.test(type)).length === 2}
                      onChange={({ target: { checked } }) => {

                        if (checked) {
                          dispatch({
                            type: 'UPDATE',
                            payload: {
                              rightsholders: state.id ? Array.from(new Set([...state.rightsholders, state.id])) : state.rightsholders,
                              org_type: [...state.org_type, orgTypeOpts.find(({ type }) => /rightsholder/i.test(type))].filter(Boolean),
                            }
                          });
                        } else {
                          dispatch({
                            type: 'UPDATE',
                            payload: {
                              rightsholders: state.id ? state.rightsholders.filter((id) => id !== state.id) : state.rightsholders,
                              org_type: state.org_type.filter(({ type }) => !/rightsholder/i.test(type)),
                            }
                          });
                        }
                      }}/>}
                    label="Own content"/>
                </FormControl>
              </>}
              {state.org_type.some(({ type }) => /publisher/i.test(type)) && <>
                <FormControl fullWidth>
                  <FormControlLabel
                    control={<Checkbox
                      defaultChecked={state.org_type.filter(({ type }) => /publisher|rightsholder/i.test(type)).length === 2}
                      onChange={({ target: { checked } }) => {

                        if (checked) {
                          dispatch({
                            type: 'UPDATE',
                            payload: {
                              rightsholders: state.id ? Array.from(new Set([...state.rightsholders, state.id])) : state.rightsholders,
                              org_type: [...state.org_type, orgTypeOpts.find(({ type }) => /rightsholder/i.test(type))].filter(Boolean),
                            }
                          });
                        } else {
                          dispatch({
                            type: 'UPDATE',
                            payload: {
                              rightsholders: state.id ? state.rightsholders.filter((id) => id !== state.id) : state.rightsholders,
                              org_type: state.org_type.filter(({ type }) => !/rightsholder/i.test(type)),
                            }
                          });
                        }
                      }}/>}
                    label="Own content"/>
                </FormControl>
              </>}
            </Grid>

            {state.org_type.some(({ type }) => /advertiser|publisher/i.test(type)) && <>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <FormLabel required id="integration-type-radio-buttons-group-label">Integration
                    type</FormLabel>
                  <RadioGroup aria-labelledby="integration-type-radio-buttons-group-label"
                              name="radio-buttons-group-integration"
                              row>
                    {integrationOpts.map(({ id, description }) => (
                      <FormControlLabel
                        value={id}
                        key={id}
                        control={<Radio required checked={state.integration_type === id}
                                        onChange={({ target: { value } }) => dispatch({
                                          type: 'UPDATE', payload: {
                                            integration_type: Number(value)
                                          }
                                        })}/>}
                        label={description}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </Grid>
            </>}

            {state.org_type.some(({ type }) => /rightsholder/i.test(type)) && <>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  {state.org_type.length === 1 && <FormControlLabel control={<Checkbox
                    onChange={({ target: { checked } }) => {
                      dispatch({ type: 'UPDATE', payload: { is_premium: checked } });
                    }}
                    defaultChecked={state.is_premium}
                  />} label="Is premium"/>}
                  <FormControlLabel control={<Checkbox defaultChecked={state.auto_send_clip_basic}
                                                       onChange={({ target: { checked } }) => {
                                                         dispatch({
                                                           type: 'UPDATE',
                                                           payload: { auto_send_clip_basic: checked }
                                                         });
                                                       }}
                  />}
                                    label="Auto send to ClipBasic"/>
                  <FormControlLabel control={<Checkbox
                    onChange={({ target: { checked } }) => {
                      dispatch({ type: 'UPDATE', payload: { manually_approve_video: !checked } });
                    }}
                    defaultChecked={!state.manually_approve_video}
                  />}
                                    label="Auto-approve clips"/>
                </FormControl>
              </Grid>

            </>}
            {state.org_type.length > 1 && <Grid item xs={6}></Grid>}
            <Grid item xs={6}>
              <FormControl fullWidth>
                <FormLabel id="logo-label">Logo</FormLabel>
                <DownloadArea
                  ref={dropArea}
                  draggable
                  onDrop={(e) => {
                    e.preventDefault();
                    dispatch({
                      type: 'UPDATE',
                      payload: { logo_filename: null }
                    });
                    dropFileCallback(e.dataTransfer.files);
                    dropArea?.current?.removeAttribute('drop-active');
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    dropArea?.current?.setAttribute('drop-active', '');
                  }}
                  onDragLeave={() => dropArea?.current?.removeAttribute('drop-active')}
                >
                  {!file && !state.logo_filename &&
                    <Grid container>
                      <Grid item xs={12}>
                        <Typography sx={{ mt: 4, mb: 2, textAlign: 'center' }}>Drag and drop image
                          you want to
                          upload here, or choose 'Add image'.</Typography>
                      </Grid>
                      <Grid item xs={12} sx={{ display: 'flex', mb: 4, justifyContent: 'center' }}>
                        <Button variant={'outlined'} startIcon={<UploadFile/>}
                                onClick={() => inputFile.current?.click()}>
                          Add image
                          <input
                            ref={inputFile}
                            type="file"
                            onChange={(e) => dropFileCallback(e.target.files)}
                            accept="image/*"
                            hidden
                          />
                        </Button>
                      </Grid>
                    </Grid>}
                  {file && <Box sx={{ mt: 2, position: 'relative' }}>
                    <img src={URL.createObjectURL(file)} alt="logo" style={{ width: '100px' }}/>
                    <SvgIcon onClick={() => setCurrentFile(undefined)}><Cancel
                      color="error"/></SvgIcon>
                  </Box>}
                  {state.logo_filename && <Box sx={{ mt: 2, position: 'relative' }}>
                    <img src={`${getAssetsBaseUrl()}/logo/${state.logo_filename}`} alt="logo"
                         style={{ width: '100px' }}/>
                    <SvgIcon onClick={() => {
                      setCurrentFile(undefined);
                      dispatch({ type: 'UPDATE', payload: { logo_filename: '' } });
                    }}><Cancel
                      color="error"/></SvgIcon>
                  </Box>}

                </DownloadArea>
              </FormControl>
            </Grid>

            {state.org_type.some(({ type }) => /advertiser|publisher/i.test(type)) && (<Grid item xs={6}>
              <Grid container>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                  <FormLabel id="rightholder">Rightsholder</FormLabel>
                  <Select multiple value={state.rightsholders}
                          onChange={({ target: { value } }) => {
                            dispatch({
                              type: 'UPDATE',
                              payload: {
                                rightsholders: value
                              }
                            });

                          }}>
                    {rightsholderOpts.map(({ id, domain }) => <MenuItem
                      key={id}
                      value={id}
                    >
                      {domain}
                    </MenuItem>)}
                  </Select>
                </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                   <FormLabel id="videoFormatLabel">Video format</FormLabel>
                  <Select
                    labelId="videoFormatLabel"
                    id="videoFormat"
                    multiple
                    required
                    value={state.rh_video_format}
                    label="User Role"
                    onChange={({ target: { value } }) => {
                      dispatch({
                        type: 'UPDATE',
                        payload: {
                          rh_video_format: value
                        }
                      });
                    }}
                  >
                    <MenuItem value={'vertical'}>vertical</MenuItem>
                    <MenuItem value={'horizontal'}>horizontal</MenuItem>
                  </Select>
                </FormControl>
                </Grid>
              </Grid>
              </Grid>)
            }

            <Grid item xs={12}>
              <FormControl fullWidth>
                <FormControlLabel
                  control={<Checkbox defaultChecked={state.assign_to_all_admin}
                                     onChange={({ target: { checked } }) => {
                                       dispatch({
                                         type: 'UPDATE',
                                         payload: {
                                           assign_to_all_admin: checked
                                         }
                                       });

                                     }}/>}
                  label="Assign access to all admins"/>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Grid container justifyContent={'flex-end'}>
                <Button color={'primary'} type={'submit'} variant={'contained-x'}>Submit</Button>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </form>
    </DialogRoot>
  </Dialog>);
}

